import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import type { DashboardAssessmentData } from '../../utils/dashboardAssessmentAdapter';
import ContentLoader from '../../components/ContentLoader';
import { CLIENT_CHART_THEME as T } from '../../constants/clientChartTheme';
import '../../assets/css/DashboardInsights.css';

interface DashboardInsightsProps {
  assessmentData: DashboardAssessmentData | null;
  loading?: boolean;
  error?: string | null;
}

const roundToTenth = (value: number) => Math.round(value * 10) / 10;

const DashboardInsights: React.FC<DashboardInsightsProps> = ({
  assessmentData,
  loading = false,
  error = null,
}) => {
  const maxRating = assessmentData?.maxRating ?? 5;

  const insights = useMemo(() => {
    const rows = assessmentData?.rows ?? [];
    const graphPoints = assessmentData?.graphPoints ?? [];
    const rated = rows.filter(
      (row) => row.currentStatusRating != null && row.futureStatusRating != null
    );

    if (rated.length === 0) {
      return null;
    }

    const average = (values: number[]) =>
      values.reduce((sum, value) => sum + value, 0) / values.length;

    const avgCurrent = average(rated.map((row) => row.currentStatusRating as number));
    const avgFuture = average(rated.map((row) => row.futureStatusRating as number));
    const avgGap = avgFuture - avgCurrent;
    const readiness =
      avgFuture > 0 ? Math.min(100, Math.round((avgCurrent / avgFuture) * 100)) : 0;

    const priorities = graphPoints
      .map((point) => ({
        capabilityId: point.capabilityId,
        name: point.capabilityName,
        current: point.currentAverage,
        future: point.futureAverage,
        gap: point.futureAverage - point.currentAverage,
      }))
      .sort((a, b) => b.gap - a.gap);

    return {
      capabilityCount: graphPoints.length,
      subCapabilityCount: rated.length,
      avgCurrent: roundToTenth(avgCurrent),
      avgFuture: roundToTenth(avgFuture),
      avgGap: roundToTenth(avgGap),
      readiness,
      priorities,
    };
  }, [assessmentData]);

  const gaugeOptions = useMemo(
    () => ({
      chart: {
        type: 'radialBar' as const,
        sparkline: { enabled: true },
        fontFamily: T.fontFamily,
      },
      theme: { mode: 'light' as const },
      plotOptions: {
        radialBar: {
          startAngle: -135,
          endAngle: 135,
          hollow: { size: '58%' },
          track: {
            background: T.gridLight,
            strokeWidth: '100%',
            margin: 4,
          },
          dataLabels: {
            name: {
              show: true,
              offsetY: 22,
              color: T.textMuted,
              fontSize: '12px',
              fontWeight: 500,
            },
            value: {
              show: true,
              offsetY: -10,
              color: T.text,
              fontSize: '28px',
              fontWeight: 600,
              formatter: (value: number) => `${Math.round(value)}%`,
            },
          },
        },
      },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'light',
          type: 'horizontal',
          shadeIntensity: 0.25,
          gradientToColors: [T.primaryLight],
          stops: [0, 100],
        },
        colors: [T.primary],
      },
      stroke: { lineCap: 'round' as const },
      labels: ['of target'],
      colors: [T.primary],
    }),
    []
  );

  if (loading) {
    return (
      <div className="dash-insights dash-chart-block">
        <ContentLoader compact label="Loading your maturity summary…" />
      </div>
    );
  }

  if (error || !insights) {
    return null;
  }

  const stats = [
    { label: 'Capabilities assessed', value: `${insights.capabilityCount}`, hint: 'focus areas' },
    { label: 'Sub-capabilities scored', value: `${insights.subCapabilityCount}`, hint: 'data points' },
    {
      label: 'Current maturity',
      value: `${insights.avgCurrent}`,
      hint: `avg of ${maxRating}`,
    },
    {
      label: 'Target maturity',
      value: `${insights.avgFuture}`,
      hint: `avg of ${maxRating}`,
    },
    {
      label: 'Maturity uplift',
      value: insights.avgGap > 0 ? `+${insights.avgGap}` : `${insights.avgGap}`,
      hint: 'current → target',
      accent: true,
    },
  ];

  const gapLabel = (gap: number) => {
    if (gap > 0) return `+${gap} to target`;
    if (gap === 0) return 'On target';
    return `${Math.abs(gap)} ahead`;
  };

  return (
    <div className="dash-insights">
      <div className="dash-insights__header">
        <h2 className="dash-chart-title app-heading-accent">Your maturity at a glance</h2>
        <p className="dash-insights__subtitle">
          A summary of where you are today and where you aim to be.
        </p>
      </div>

      <div className="dash-insights__stats">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className={`dash-insight-stat${stat.accent ? ' dash-insight-stat--accent' : ''}`}
          >
            <span className="dash-insight-stat__value">{stat.value}</span>
            <span className="dash-insight-stat__label">{stat.label}</span>
            <span className="dash-insight-stat__hint">{stat.hint}</span>
          </div>
        ))}
      </div>

      <div className="dash-insights__body">
        <div className="dash-insights__gauge">
          <Chart
            options={gaugeOptions}
            series={[insights.readiness]}
            type="radialBar"
            height={230}
          />
          <p className="dash-insights__gauge-caption">
            Overall maturity vs. your target
          </p>
        </div>

        <div className="dash-insights__priorities">
          <h3 className="dash-insights__priorities-title app-heading-accent">
            Priority opportunities
          </h3>
          <p className="dash-insights__priorities-subtitle">
            Capabilities with the largest gap between today and your target.
          </p>
          <div className="dash-insight-priority-list">
            {insights.priorities.slice(0, 5).map((priority) => {
              const currentPct = Math.max(
                0,
                Math.min(100, (priority.current / maxRating) * 100)
              );
              const futurePct = Math.max(
                0,
                Math.min(100, (priority.future / maxRating) * 100)
              );
              const gapWidth = Math.max(0, futurePct - currentPct);

              return (
                <div className="dash-insight-priority" key={priority.capabilityId}>
                  <div className="dash-insight-priority__head">
                    <span className="dash-insight-priority__name" title={priority.name}>
                      {priority.name}
                    </span>
                    <span
                      className={`dash-insight-priority__gap${
                        priority.gap > 0 ? ' is-open' : ''
                      }`}
                    >
                      {gapLabel(priority.gap)}
                    </span>
                  </div>
                  <div className="dash-insight-bar" aria-hidden="true">
                    <div
                      className="dash-insight-bar__current"
                      style={{ width: `${currentPct}%` }}
                    />
                    <div
                      className="dash-insight-bar__gap"
                      style={{ left: `${currentPct}%`, width: `${gapWidth}%` }}
                    />
                  </div>
                  <div className="dash-insight-bar__scale">
                    <span>Now {priority.current}</span>
                    <span>Target {priority.future}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardInsights;
